import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import CustomSelect from '../../../ui/CustomSelect';

const statusOptions = [
  { value: 'active', label: 'Aktif' },
  { value: 'inactive', label: 'Tidak Aktif' },
];

export default function SalaryGradeModal({
  open,
  data,
  departments,
  subDivisions,
  positions,
  saving,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState(data || {});

  useEffect(() => {
    setForm(data || {});
  }, [data]);

  if (!open) return null;

  const handleSubmit = e => {
    e.preventDefault();
    onSave({
      ...form,
      min_salary: form.min_salary ? parseFloat(form.min_salary) : null,
      max_salary: form.max_salary ? parseFloat(form.max_salary) : null,
    });
  };

  const toggleArray = (field, id) => {
    const arr = form[field] || [];
    const newArr = arr.includes(id) ? arr.filter(i => i !== id) : [...arr, id];
    setForm({ ...form, [field]: newArr });
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden animate-modal">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">{form?.id ? 'Edit' : 'Tambah'} Grade Gaji</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Grade <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form?.grade || ''}
                onChange={e => setForm({ ...form, grade: e.target.value })}
                placeholder="A, B, C, 1, 2, 3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Nama Grade <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form?.name || ''}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Gaji Minimum (Rp) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={form?.min_salary || ''}
                onChange={e => setForm({ ...form, min_salary: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Gaji Maximum (Rp) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={form?.max_salary || ''}
                onChange={e => setForm({ ...form, max_salary: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                required
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <label className="text-sm font-medium text-gray-700">Status</label>
              <CustomSelect
                value={form?.status || 'active'}
                onChange={val => setForm({ ...form, status: val })}
                options={statusOptions}
                placeholder="Pilih status"
                width="w-full"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Deskripsi</label>
            <textarea
              value={form?.description || ''}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
            />
          </div>

          <div className="border-t pt-4 space-y-4">
            <h4 className="font-semibold">Mapping Organisasi</h4>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Divisi</label>
                <div className="border rounded-lg p-2 max-h-32 overflow-y-auto space-y-1">
                  {departments.map(dept => (
                    <label
                      key={dept.id}
                      className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={form?.division_ids?.includes(dept.id) || false}
                        onChange={() => toggleArray('division_ids', dept.id)}
                        className="w-4 h-4"
                      />
                      {dept.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Sub Divisi</label>
                <div className="border rounded-lg p-2 max-h-32 overflow-y-auto space-y-1">
                  {subDivisions.map(sub => (
                    <label
                      key={sub.id}
                      className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={form?.sub_division_ids?.includes(sub.id) || false}
                        onChange={() => toggleArray('sub_division_ids', sub.id)}
                        className="w-4 h-4"
                      />
                      {sub.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Jabatan</label>
                <div className="border rounded-lg p-2 max-h-32 overflow-y-auto space-y-1">
                  {positions.map(pos => (
                    <label
                      key={pos.id}
                      className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={form?.position_ids?.includes(pos.id) || false}
                        onChange={() => toggleArray('position_ids', pos.id)}
                        className="w-4 h-4"
                      />
                      {pos.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 font-medium"
          >
            Batal
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 font-medium"
          >
            {saving ? 'Menyimpan...' : 'Simpan'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
